import { useMemo } from 'react'

import type { ToolbarContractListItem } from '#/shared/api/generated/shipment/models'
import {
  useListContractsApiV1ContractsGet,
  useListItemsApiV1ToolbarTypeGet,
} from '#/shared/api/generated/shipment/theCargoShipment'

const DEFAULT_CONTRACT_FEED_SIZE = 100

export interface ShipmentContractFeedItem {
  id: string
  contractType: string
  status: string
  recipient: string
  sentAt: string
  signed: boolean
  signedAt: string | undefined
  viewedAt: string | undefined
  previewUrl: string | undefined
  expiresAt: string | undefined
  isApp: boolean
  cca: boolean
  note: string | undefined
  /** Customer e-signature URL — host is env-configured, so never build it on the FE. */
  signLink: string | undefined
}

interface UseShipmentContractFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentContractFeedResult {
  contracts: ShipmentContractFeedItem[]
  isLoading: boolean
}

function nullToUndef<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined
}

export function toShipmentContractFeedItem(
  contract: ToolbarContractListItem,
  signLinks: Record<string, string> = {},
): ShipmentContractFeedItem {
  return {
    id: contract.id,
    contractType: contract.contract_type,
    status: contract.status,
    recipient: contract.sent_to_email ?? '',
    sentAt: contract.sent_at ?? contract.created_at,
    signed: contract.signed ?? false,
    signedAt: nullToUndef(contract.signed_at),
    viewedAt: nullToUndef(contract.viewed_at),
    previewUrl: nullToUndef(contract.preview_url),
    expiresAt: nullToUndef(contract.expires_at),
    isApp: contract.is_app ?? false,
    cca: contract.cca ?? false,
    note: nullToUndef(contract.note),
    signLink: signLinks[contract.id],
  }
}

export function useShipmentContractFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_CONTRACT_FEED_SIZE,
  }: UseShipmentContractFeedOptions = {},
): UseShipmentContractFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'contract',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  // The toolbar feed doesn't carry `sign_link`, so it's joined in from the
  // contracts list, which returns the env-specific customer e-sign URL.
  const linksQuery = useListContractsApiV1ContractsGet(
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const contracts = useMemo<ShipmentContractFeedItem[]>(() => {
    const signLinks: Record<string, string> = {}
    for (const contract of linksQuery.data?.data.items ?? []) {
      if (contract.sign_link) signLinks[contract.id] = contract.sign_link
    }
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarContractListItem[]
    return items.map((item) => toShipmentContractFeedItem(item, signLinks))
  }, [query.data, linksQuery.data])

  return {
    contracts,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
